// frontend/src/pages/StudentReportPage.tsx

import { Link } from 'react-router-dom';
import {
  ArrowLeft, Star, Flame, BookOpen, MessageSquare, FileText,
  TrendingUp, CheckCircle, AlertTriangle, Lightbulb, Trophy
} from 'lucide-react';
import { useStudentReport, ReportCritique } from '../hooks/useStudentReport';
import { useAuthStore } from '../store/authStore';
import '../styles/student-report.css';

const CATEGORY_EMOJI: Record<string, string> = {
  python: '🐍', web: '🌐', scratch: '🎮', ai: '🧠',
  yapay_zeka: '🤖', game: '🕹️', matematik: '📐',
  fen: '🔬', ingilizce: '🇬🇧', turkce: '📚', zeka: '💡',
};

const TONE_CONFIG: Record<ReportCritique['tone'], { color: string; bg: string; label: string; emoji: string }> = {
  excellent:   { color: '#059669', bg: '#D1FAE5', label: 'Mükemmel', emoji: '🏆' },
  good:        { color: '#0B8F70', bg: '#CCFBF1', label: 'İyi',      emoji: '⭐' },
  fair:        { color: '#D97706', bg: '#FEF3C7', label: 'Orta',     emoji: '💪' },
  needs_work:  { color: '#DC2626', bg: '#FEE2E2', label: 'Çalışma Gerekli', emoji: '📚' },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('tr-TR', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function ScoreRing({ score }: { score: number }) {
  const r = 52;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;

  return (
    <svg width="130" height="130" className="sr-score-ring">
      <circle cx="65" cy="65" r={r} fill="none" stroke="#E2E8F0" strokeWidth="12" />
      <circle
        cx="65" cy="65" r={r}
        fill="none"
        stroke="url(#scoreGrad)"
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        transform="rotate(-90 65 65)"
        style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.16,1,0.3,1)' }}
      />
      <defs>
        <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0B8F70" />
          <stop offset="100%" stopColor="#17C79A" />
        </linearGradient>
      </defs>
      <text x="65" y="61" textAnchor="middle" fontSize="24" fontWeight="800" fill="#10142A" fontFamily="Plus Jakarta Sans, sans-serif">
        {score}
      </text>
      <text x="65" y="77" textAnchor="middle" fontSize="11" fill="#64748B" fontFamily="Plus Jakarta Sans, sans-serif">
        / 100
      </text>
    </svg>
  );
}



function ActivityBar({ days }: { days: { date: string; lessonCount: number; noteCount: number; aiCount: number }[] }) {
  const dayMap = new Map(days.map(d => [d.date, d]));

  const last14Days = [];
  const today = new Date();
  for (let i = 13; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);
    const existing = dayMap.get(dateStr) || {
      date: dateStr,
      lessonCount: 0,
      noteCount: 0,
      aiCount: 0
    };
    last14Days.push(existing);
  }

  const maxTotal = Math.max(1, ...last14Days.map(d => d.lessonCount + d.noteCount + d.aiCount));

  return (
    <div className="sr-activity-bars">
      {last14Days.map(day => {
        const total = day.lessonCount + day.noteCount + day.aiCount;
        const heightPct = Math.round((total / maxTotal) * 100);
        
        let barColor = 'linear-gradient(180deg, var(--teal), var(--teal-deep))';
        if (total > 0) {
          if (day.lessonCount > 0 && day.noteCount === 0 && day.aiCount === 0) {
            barColor = 'linear-gradient(180deg, #10B981, #059669)';
          } else if (day.noteCount > 0 && day.lessonCount === 0 && day.aiCount === 0) {
            barColor = 'linear-gradient(180deg, #6366F1, #4F46E5)';
          } else if (day.aiCount > 0 && day.lessonCount === 0 && day.noteCount === 0) {
            barColor = 'linear-gradient(180deg, #3B82F6, #2563EB)';
          } else {
            barColor = 'linear-gradient(180deg, #3b82f6, #10b981)';
          }
        } else {
          barColor = 'rgba(226, 232, 240, 0.4)';
        }

        const dateObj = new Date(day.date);
        const dayLabel = dateObj.toLocaleDateString('tr-TR', { weekday: 'short' });

        return (
          <div
            key={day.date}
            className="sr-activity-bar-wrap"
            title={`${dateObj.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}: ${total} aktivite (Ders: ${day.lessonCount}, Not: ${day.noteCount}, AI: ${day.aiCount})`}
          >
            <div 
              className="sr-activity-bar" 
              style={{ 
                height: total > 0 ? `${Math.max(8, heightPct)}%` : '6px',
                background: barColor,
                borderRadius: total > 0 ? '4px 4px 0 0' : '50%'
              }} 
            />
            <span className="sr-activity-bar-day">
              {dayLabel}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function StudentReportPage() {
  const profile = useAuthStore((s) => s.profile);
  const { data, isLoading, error } = useStudentReport();

  if (isLoading) {
    return (
      <div className="sr-page">
        <div className="sr-loading">
          <div className="sr-loading-spinner" />
          <span>Rapor hazırlanıyor...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="sr-page">
        <div className="sr-error">
          <span>⚠️</span>
          <p>{error ?? 'Rapor yüklenemedi.'}</p>
          <Link to="/dashboard" className="sr-back-btn">Dashboard'a Dön</Link>
        </div>
      </div>
    );
  }

  const toneConf = TONE_CONFIG[data.critique.tone];
  const firstName = profile?.full_name?.split(' ')[0] ?? 'Öğrenci';

  return (
    <div className="sr-page">
      {/* Back button */}
      <Link to="/dashboard" className="sr-back-link">
        <ArrowLeft size={18} />
        Dashboard'a Dön
      </Link>

      {/* Page header */}
      <div className="sr-header">
        <div>
          <h1 className="sr-title">📊 {firstName}'ın Öğrenme Raporu</h1>
          <p className="sr-subtitle">Son 30 günün genel değerlendirmesi</p>
        </div>
      </div>

      {/* ─── TOP GRID: Score + Quick Stats ─── */}
      <div className="sr-top-grid">
        {/* Genel Skor Kartı */}
        <div className="sr-card sr-score-card">
          <h2 className="sr-card-title">Genel Performans Skoru</h2>
          <div className="sr-score-body">
            <ScoreRing score={data.critique.overallScore} />
            <div className="sr-score-info">
              <span
                className="sr-tone-badge"
                style={{ background: toneConf.bg, color: toneConf.color }}
              >
                {toneConf.emoji} {toneConf.label}
              </span>
              <p className="sr-score-summary">{data.critique.summary}</p>
            </div>
          </div>
        </div>

        {/* Hızlı istatistikler */}
        <div className="sr-quick-stats">
          <div className="sr-stat-card">
            <div className="sr-stat-icon" style={{ background: '#10B981' }}>
              <BookOpen size={20} color="#fff" />
            </div>
            <div className="sr-stat-val">{data.totalLessonsCompleted}</div>
            <div className="sr-stat-lbl">Tamamlanan Ders</div>
          </div>
          <div className="sr-stat-card">
            <div className="sr-stat-icon" style={{ background: '#F59E0B' }}>
              <Flame size={20} color="#fff" />
            </div>
            <div className="sr-stat-val">{data.currentStreak}</div>
            <div className="sr-stat-lbl">Günlük Seri</div>
          </div>
          <div className="sr-stat-card">
            <div className="sr-stat-icon" style={{ background: '#3B82F6' }}>
              <MessageSquare size={20} color="#fff" />
            </div>
            <div className="sr-stat-val">{data.totalAiConversations}</div>
            <div className="sr-stat-lbl">AI Mentor Sorusu</div>
          </div>
          <div className="sr-stat-card">
            <div className="sr-stat-icon" style={{ background: '#6366F1' }}>
              <FileText size={20} color="#fff" />
            </div>
            <div className="sr-stat-val">{data.totalNotes}</div>
            <div className="sr-stat-lbl">Alınan Not</div>
          </div>
          <div className="sr-stat-card">
            <div className="sr-stat-icon" style={{ background: '#EC4899' }}>
              <Star size={20} color="#fff" />
            </div>
            <div className="sr-stat-val">{data.xp.toLocaleString('tr-TR')}</div>
            <div className="sr-stat-lbl">Toplam XP</div>
          </div>
          <div className="sr-stat-card">
            <div className="sr-stat-icon" style={{ background: '#0B8F70' }}>
              <TrendingUp size={20} color="#fff" />
            </div>
            <div className="sr-stat-val">{data.activeDaysCount}</div>
            <div className="sr-stat-lbl">Aktif Gün (30g)</div>
          </div>
        </div>
      </div>

      {/* ─── Yapıcı Eleştiri ─── */}
      <div className="sr-card sr-critique-card">
        <h2 className="sr-card-title">🎯 Kişisel Değerlendirme</h2>
        <div className="sr-critique-list">
          {data.critique.points.map((point, i) => {
            const Icon = point.type === 'positive' ? CheckCircle
              : point.type === 'warning' ? AlertTriangle
              : Lightbulb;
            const color = point.type === 'positive' ? '#10B981'
              : point.type === 'warning' ? '#F59E0B'
              : '#3B82F6';
            return (
              <div key={i} className="sr-critique-item" style={{ borderLeftColor: color }}>
                <Icon size={18} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
                <p>{point.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Son 14 Gün Aktivite + Kurs İlerleme ─── */}
      <div className="sr-mid-grid">
        {/* Aktivite grafiği */}
        <div className="sr-card">
          <h2 className="sr-card-title">📅 Son 14 Gün Aktivitesi</h2>
          {data.activeDays.length === 0 ? (
            <p className="sr-empty-text">Son 14 günde aktivite kaydedilmedi.</p>
          ) : (
            <ActivityBar days={data.activeDays} />
          )}
          <div className="sr-activity-legend">
            <span className="sr-legend-dot" style={{ background: '#17C79A' }} /> Ders
            <span className="sr-legend-dot" style={{ background: '#6366F1' }} /> Not
            <span className="sr-legend-dot" style={{ background: '#3B82F6' }} /> AI Soru
          </div>
        </div>

        {/* AI Mentor soruları */}
        <div className="sr-card">
          <h2 className="sr-card-title">🤖 Son AI Mentor Sohbetleri</h2>
          {data.recentAiTopics.length === 0 ? (
            <div className="sr-empty-state">
              <p>Henüz AI Mentor kullanılmadı.</p>
              <Link to="/ai-mentor" className="sr-cta-btn">AI Mentor'u Dene →</Link>
            </div>
          ) : (
            <div className="sr-ai-list">
              {data.recentAiTopics.map(topic => (
                <div key={topic.id} className="sr-ai-item">
                  <div className="sr-ai-icon">🤖</div>
                  <div className="sr-ai-content">
                    <span className="sr-ai-title">{topic.title}</span>
                    <span className="sr-ai-date">{formatDate(topic.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── Kurs İlerlemesi ─── */}
      <div className="sr-card">
        <h2 className="sr-card-title">📚 Ders İlerlemesi</h2>
        {data.courseProgress.length === 0 ? (
          <div className="sr-empty-state">
            <p>Henüz aktif bir ders yok.</p>
            <Link to="/courses" className="sr-cta-btn">Derslere Göz At →</Link>
          </div>
        ) : (
          <div className="sr-courses-list">
            {data.courseProgress.map(course => {
              const emoji = CATEGORY_EMOJI[course.category] ?? '📘';
              const statusLabel = course.status === 'completed' ? 'Tamamlandı'
                : course.status === 'in_progress' ? 'Devam ediyor'
                : 'Başlanmadı';
              const statusColor = course.status === 'completed' ? '#10B981'
                : course.status === 'in_progress' ? '#3B82F6'
                : '#64748B';

              return (
                <div key={course.courseId} className="sr-course-row">
                  <div className="sr-course-emoji">{emoji}</div>
                  <div className="sr-course-info">
                    <div className="sr-course-top">
                      <span className="sr-course-name">{course.title}</span>
                      <span className="sr-course-status" style={{ color: statusColor }}>
                        {course.status === 'completed' && <Trophy size={13} />}
                        {statusLabel}
                      </span>
                    </div>
                    <div className="sr-course-bar-row">
                      <div className="sr-course-bar-wrap">
                        <div
                          className="sr-course-bar-fill"
                          style={{ width: `${course.progressPercent}%` }}
                        />
                      </div>
                      <span className="sr-course-pct">%{course.progressPercent}</span>
                    </div>
                    <span className="sr-course-lessons">
                      {course.completedLessons}/{course.totalLessons} ders tamamlandı
                      {course.lastActivityAt && ` · Son: ${formatDate(course.lastActivityAt)}`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
